import React, { useEffect, useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { ChildDto, ImportDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { fetchChildren, getGroupsWithLocationName, updateChildStatus } from '../../../api/kindergarten.api';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as FaceIcon } from '../../../assets/face-icon.svg';
import { ReactComponent as ListEmptyStateIcon } from '../../../assets/list-empty-state-icon.svg';
import { GeneralStatusEnum, ImportUserType, SortOrderEnum } from '../../../common/enums/common.enum';
import GenericInfoModal from '../../../components/GenericInfoModal';
import ToggleWithLabel from '../../../components/ToggleWithLabel';
import { ChildrenTabSortKeyEnum } from '../../../common/enums/kindergarten.enum';
import GenericMultiSelectWithSearch from '../../../components/GenericMultiSelectWithSearch';
import GenericImportButton from '../../../components/GenericImportButton';

export const CHILDREN_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'Name', sortable: true, sortKey: ChildrenTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'Birthday', sortable: true, sortKey: ChildrenTabSortKeyEnum.BIRTHDAY },
  { id: 3, value: '', label: 'Parents', sortable: true, sortKey: ChildrenTabSortKeyEnum.PARENTS, width: '25%' },
  { id: 4, value: '', label: 'Groups', sortable: true, sortKey: ChildrenTabSortKeyEnum.GROUPS },
  { id: 5, value: '', label: 'Status', sortable: true, sortKey: ChildrenTabSortKeyEnum.STATUS },
  { id: 6, value: '', label: '', sortable: false }
];

const ChildrenTab: React.FC<{ kindergartenId: string; kindergartenName: string }> = ({ kindergartenId, kindergartenName }) => {
  const [children, setChildren] = useState<ChildDto[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const [withParents, setWithParents] = useState<boolean>(false);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState<string>('');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<ChildrenTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [childToDeactivate, setChildToDeactivate] = useState<ChildDto | null>(null);
  const [importWarning, setImportWarning] = useState<ImportDto[]>();

  useEffect(() => {
    loadGroups();
  }, [kindergartenId]);

  useEffect(() => {
    loadChildren();
  }, [kindergartenId, metadata.currentPage, metadata.itemsPerPage, searchParams, selectedGroupIds, sortBy, sortOrder, withParents]);

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    if (sortKey === 'parents' && !withParents) return;
    setSortBy(sortKey ? (sortKey as ChildrenTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : undefined);
  };

  const loadChildren = async () => {
    setLoader(true);
    try {
      const result = await fetchChildren(
        kindergartenId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams,
        selectedGroupIds,
        withParents
      );
      setChildren(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'An error occurred while fetching children');
    } finally {
      setLoader(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await getGroupsWithLocationName(kindergartenId);
      if (response?.results) {
        setGroups(response.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch groups');
    }
  };

  const handleChangeChildStatus = async (childId: string) => {
    if (!kindergartenId) return;

    try {
      const response = await updateChildStatus(kindergartenId, childId);
      if (response.message) {
        createNotification(MESSAGE_TYPE.SUCCESS, response.message);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to change status for child');
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!childToDeactivate) return;
    handleChangeChildStatus(childToDeactivate.id);
    setChildren(prev =>
      prev.map(t => (t.id === childToDeactivate.id ? { ...t, status: GeneralStatusEnum.INACTIVE, group: undefined } : t))
    );
    setChildToDeactivate(null);
  };

  const handleCreateClick = () => {
    navigate(`/${kindergartenId}/child/create/${encodeURIComponent(kindergartenName)}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24,
            color: 'text.primary',
            fontFamily: 'Poppins'
          }}
        >
          Children
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GenericImportButton
            label="Import"
            type={ImportUserType.CHILDREN}
            kindergartenId={kindergartenId}
            onSuccessImport={loadChildren}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label="Create"
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          border: '1px solid #F0F0F0',
          borderRadius: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <TableHeader searchTitle="Search by name" sortOptions={[]} setSearchValue={handleSearchChange} />
          <GenericMultiSelectWithSearch
            label="Group"
            options={groups}
            onClearAll={async () => {
              setSelectedGroupIds([]);
            }}
            onShowResults={async ids => {
              setSelectedGroupIds(ids);
              setMetadata(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
          <ToggleWithLabel label="With parents" checked={withParents} onChange={setWithParents} />
        </Box>

        <BasicTable
          headings={CHILDREN_TABLE_HEADINGS}
          loader={loader}
          metadata={metadata}
          setMetadata={setMetadata}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        >
          {children.map(child => (
            <TableRow
              key={child.id}
              hover
              sx={{ cursor: ' pointer' }}
              onClick={() => navigate(`/${kindergartenId}/child/${child.id}/details/${encodeURIComponent(kindergartenName)}`)}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {child.profileImage ? (
                    <img
                      src={child.profileImage}
                      alt={`${child.firstName} ${child.middleName} ${child.lastName}`}
                      style={{ color: 'text.primary', width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <FaceIcon style={{ width: 34, height: 34 }} />
                  )}
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 400 }}>
                    {`${child.firstName} (${child.middleName}) ${child.lastName}`}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{child.birtDate?.split('T')[0]}</TableCell>
              <TableCell>{child.parents?.map((g: any) => g.fullName).join(', ') ?? '-'}</TableCell>
              <TableCell>{child.group ? child.group.name : '-'}</TableCell>
              <TableCell>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <GenericStatusSwitch
                    status={child.status}
                    onChange={() => {
                      if (child.status === GeneralStatusEnum.ACTIVE) {
                        setChildToDeactivate(child);
                      } else {
                        handleChangeChildStatus(child.id);
                        setChildren(prev =>
                          prev.map(t =>
                            t.id === child.id
                              ? {
                                  ...t,
                                  status: t.status === GeneralStatusEnum.ACTIVE ? GeneralStatusEnum.INACTIVE : GeneralStatusEnum.ACTIVE
                                }
                              : t
                          )
                        );
                      }
                    }}
                    showStatusText={false}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <EditIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/${kindergartenId}/child/${child.id}/edit/${encodeURIComponent(kindergartenName)}`);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
          {childToDeactivate && (
            <GenericInfoModal
              open={true}
              onClose={() => setChildToDeactivate(null)}
              onConfirm={handleConfirmDeactivation}
              label="Deactivate child"
              message="Are you sure you want to deactivate "
              confirmLabel="Deactivate"
              entityName={`${childToDeactivate.firstName} (${childToDeactivate.middleName}) ${childToDeactivate.lastName}`}
            />
          )}
        </BasicTable>
      </Box>
    </Box>
  );
};
export default ChildrenTab;
